import React, { useEffect, useRef } from 'react';
import { ThemeProvider } from '@primer/react';
import { Toaster } from 'react-hot-toast';
import { BrowserRouter } from 'react-router-dom';
import { useSelector } from 'react-redux';
import LTTheme from './LTTheme';

import LTCommonHeader from '@lambdatestincprivate/lt-common-header';
import '@lambdatestincprivate/lt-common-header/build/styles.css';
import Loader from 'components/Loader';
import RoutesWrapper from '../../routes';

import CustomHeader from './components/custom-header';

import AppBannerAndModals from './modals';
import useFeatureFlags from 'hooks/useFeatureFlags';

// import ErrorBoundary from 'new-theme/components/ErrorBoundary';

import { toasterStyle } from './constants';
import { FF_ACCESSIBILITY_TOOLS } from 'constants/feature-flags';

import './style.css';
import './high-contrast.css';
import useAppWithHeader from './useAppWithHeader';
import useApp from 'hooks/useApp';
import {
  LTIconButton,
  LTToast,
  LTTooltip,
} from '@lambdatestincprivate/lt-components';
import { Icon } from 'components/Icons/new-theme';
import AccessKeyPopup from 'components/AccessKeyPopUp';
import { EventNames } from 'utils/new-theme-events';
import { sendAmplitudeEvents } from 'utils/amplitude';
import { isOnPremURL } from 'utils/brandUrl';

function App() {
  const { userInfo = {}, orgPreference = {} } = useApp();
  const featureFlags = useFeatureFlags();

  const { data: userInfoData } = userInfo;

  const { error: opError } = orgPreference;

  let areFeatureFlagsLoading =
    !featureFlags?.normalFFFetched || !featureFlags?.amplitudeFFFetched;

  if (isOnPremURL() && featureFlags?.normalFFFetched) {
    areFeatureFlagsLoading = false;
  }

  const isThingsLoading =
    !userInfoData || opError === null || areFeatureFlagsLoading;

  const wcagOn = useSelector(
    (state) => state?.featureFlag?.[FF_ACCESSIBILITY_TOOLS]
  );

  useEffect(() => {
    sessionStorage.setItem('back_action', 'push');
  }, []);

  const timeRef = useRef(0);
  const clickRef = useRef(0);

  function handleClick(e) {
    e.stopPropagation();
    const timeToMatch = timeRef.current;
    const curTime = new Date().getTime();
    const dif = curTime - timeToMatch;

    const header = document.getElementsByClassName('common-header-title')[0];
    let isRightElement = e.target.contains(header);

    if (clickRef.current >= 4 && dif <= 1000 && isRightElement) {
      clickRef.current = 0;
      timeRef.current = 0;
    } else {
      timeRef.current = new Date().getTime();
      clickRef.current += 1;
    }
  }

  useEffect(() => {
    if (wcagOn && window?.toast?.extendDuration !== undefined) {
      window.toast.extendDuration = wcagOn;
    }
  }, [wcagOn]);

  useEffect(() => {
    let header;
    setTimeout(() => {
      header = document.getElementsByClassName('common-header-title')[0];
      header?.addEventListener('click', handleClick);
    }, 3000);

    return () => {
      header?.removeEventListener('click', handleClick);
      clickRef.current = 0;
      timeRef.current = 0;
    };
  }, []);

  if (isThingsLoading) {
    return <Loader isSuspenseLoader={true} />;
  }

  function sendNotification(addToast) {
    window.toast = {
      addToast,
      extendDuration: wcagOn,
      toastVariants: LTToast.ToastVariants,
      showCloseBtn: true,
    };
  }

  let appContent = (
    <BrowserRouter>
      <Toaster containerStyle={toasterStyle} position="top-right" />
      <LTToast sendNotification={sendNotification} />
      <AppWithHeader wcagOn={wcagOn} showNewTheme={true}>
        <Content />
      </AppWithHeader>
    </BrowserRouter>
  );

  appContent = (
    <ThemeProvider
      dayScheme={wcagOn ? 'light_high_contrast' : 'light'}
      theme={LTTheme}
    >
      {appContent}
    </ThemeProvider>
  );

  return appContent;
}

export default App;

function AppWithHeader({ children, showNewTheme, wcagOn }) {
  const { concurrencyToRender } = useAppWithHeader();

  //return <div className="wrapper"> {children}</div>;

  const goToAnalytics = () => {
    sendAmplitudeEvents(EventNames.HYP_CLICK_ANALYTICS);
    window.location.href = process.env.REACT_APP_ANALYTICS_URL;
  };

  const timeoutMinutes =
    window?._env_?.REACT_APP_IDLE_TIMEOUT_IN_MINUTES ||
    (process && process?.env?.REACT_APP_IDLE_TIMEOUT_IN_MINUTES) ||
    0;

  let ANNOUNCEMENT_API_URL = '';
  if (window?._env_?.REACT_APP_LUMS_AUTH_URL && isOnPremURL()) {
    let lumsURL = window?._env_?.REACT_APP_LUMS_AUTH_URL;
    lumsURL = lumsURL.replace('auth', 'supportportal');

    ANNOUNCEMENT_API_URL = `${lumsURL}/api/v1/announcement`;
  }

  return (
    <LTCommonHeader
      announcementUrl={ANNOUNCEMENT_API_URL || undefined}
      customProductHeader={<CustomHeader />}
      isHighContrastThemeEnabled={wcagOn}
      concurrencyInfo={concurrencyToRender}
      showTunnel={true}
      isNewThemeEnabled={showNewTheme}
      timeoutMinutes={timeoutMinutes ?? 0}
      onPremPrefixToUse={window?._env_?.REACT_APP_ONPREM_PREFIX || undefined}
      lowResNavItems={[
        {
          icon: <Icon name="analytics" />,
          label: 'Analytics',
          onClick: goToAnalytics,
        },
        {
          icon: <Icon name="accesskey" />,
          label: 'Access Key',
          contentToShow: <AccessKeyPopup renderOnlyInnerContent={true} />,
        },
      ]}
      topNavButtons={
        <div className="lt-header-two-btns">
          <LTTooltip direction={LTTooltip.Direction.BOTTOM} text="Analytics">
            <LTIconButton
              aria-label="Go to Analytics"
              icon={() => <Icon name="analytics" />}
              onClick={goToAnalytics}
              size={LTIconButton.Size.SMALL}
            />
          </LTTooltip>

          <LTTooltip direction={LTTooltip.Direction.BOTTOM} text="Access Key">
            <AccessKeyPopup />
          </LTTooltip>
        </div>
      }
    >
      {children}
    </LTCommonHeader>
  );
}

function Content() {
  let promoBannerHeight =
    useSelector((state) => state.appSetting?.setting?.promoBannerHeight) || 0;

  const showHighContrast = useSelector(
    (state) => state?.featureFlag?.[FF_ACCESSIBILITY_TOOLS]
  );

  let className = 'main-section new-theme';

  useEffect(() => {
    if (showHighContrast) {
      document
        .getElementsByTagName('body')[0]
        .classList?.add('lt-contrast-theme-body');
      removeAnnouncekit();
    } else {
      insertAnnouncekit();
    }
  }, [showHighContrast]);

  className +=
    promoBannerHeight > 0
      ? ' newHeaderContentWrapperWithBanner'
      : ' newHeaderContentWrapper special';

  if (showHighContrast) {
    className += ' lt-contrast-theme';
  }

  function insertAnnouncekit() {
    const script = document.createElement('script');
    script.src = '//cdn.announcekit.app/widget.js';
    script.id = 'lt-announcekit';
    script.type = 'text/javascript';
    script.async = true;
    document.head.appendChild(script);
  }

  function removeAnnouncekit() {
    const scriptElement = document.getElementById('lt-announcekit');
    if (scriptElement) {
      scriptElement.remove();
    }
  }

  return (
    <React.Suspense fallback={<Loader isSuspenseLoader={true} />}>
      <main className={className} id="app-main-section">
        <AppBannerAndModals />
        <RoutesWrapper />
      </main>
    </React.Suspense>
  );
}
